import { useState, type FormEvent, type ReactNode } from 'react';
import { CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { Button, Input, Typography, type InputProps } from '@inference/ui';
import companyLogo from '@/assets/company-logo.svg';
import authHeroPanel from '@/assets/auth-hero-panel.svg';
import type { PasswordRule } from '@/lib/password-rules';

export type AuthShellProps = {
  title: string;
  subtitle?: string;
  children: ReactNode;
};

/** Inference page-frame + onboarding split (form left, hero right). */
export function AuthShell({ title, subtitle, children }: AuthShellProps) {
  return (
    <main className="flex min-h-screen bg-background">
      {/* max-w 1380px — inference page-frame */}
      <div className="box-border mx-auto flex min-h-screen w-full max-w-[86.25rem] px-space-80 transition-[max-width,padding-left,padding-right] duration-300 ease-in-out">
        <section className="flex w-full flex-col py-space-80 lg:w-1/2 lg:pr-space-40">
          <div className="mb-space-40 flex items-center gap-space-8">
            <img className="size-space-24" src={companyLogo} alt="" />
            <Typography variant="text-md" weight="medium">
              Nava Inference
            </Typography>
          </div>
          <div className="flex w-full max-w-[35rem] flex-col gap-space-24">
            {/* max-w 560px — Figma form column (header-lg / tertiary subtitle) */}
            <div className="flex flex-col gap-space-8">
              <Typography variant="header-lg" weight="semibold" as="h1">
                {title}
              </Typography>
              {subtitle ? (
                <Typography variant="header-lg" weight="semibold" className="text-tertiary">
                  {subtitle}
                </Typography>
              ) : null}
            </div>
            {children}
          </div>
        </section>
        <aside className="hidden py-space-24 lg:flex lg:w-1/2" aria-hidden="true">
          {/* min-h 832px — Figma hero panel */}
          <div className="relative h-full min-h-[52rem] w-full overflow-hidden rounded-lg">
            <img className="absolute inset-0 size-full object-cover" src={authHeroPanel} alt="" />
          </div>
        </aside>
      </div>
    </main>
  );
}

export type FieldProps = Omit<InputProps, 'onChange' | 'size' | 'type'> & {
  type?: 'text' | 'email' | 'password';
  size?: InputProps['size'];
  onChange?: (value: string) => void;
};

export function Field({
  type = 'text',
  size = 'xl',
  required = true,
  onChange,
  ...rest
}: FieldProps) {
  return (
    <Input
      type={type}
      size={size}
      required={required}
      onChange={(event) => onChange?.(event.target.value)}
      {...rest}
    />
  );
}

export type PasswordFieldProps = Omit<InputProps, 'onChange' | 'size' | 'type' | 'trailing'> & {
  size?: InputProps['size'];
  onChange: (value: string) => void;
};

export function PasswordField({
  size = 'xl',
  autoComplete = 'current-password',
  required = true,
  onChange,
  ...rest
}: PasswordFieldProps) {
  const [revealed, setRevealed] = useState(false);

  return (
    <Input
      {...rest}
      size={size}
      type={revealed ? 'text' : 'password'}
      autoComplete={autoComplete}
      required={required}
      onChange={(event) => onChange(event.target.value)}
      trailing={
        <button
          type="button"
          className="cursor-pointer text-tertiary hover:text-foreground"
          aria-label={revealed ? 'Hide password' : 'Show password'}
          aria-controls={rest.id}
          onClick={() => setRevealed((current) => !current)}
        >
          {revealed ? <EyeOff className="size-icon-md" /> : <Eye className="size-icon-md" />}
        </button>
      }
    />
  );
}

export type AuthFormProps = {
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  children: ReactNode;
  submitLabel: string;
  submitting?: boolean;
  disabled?: boolean;
  error?: string | null;
};

export function AuthForm({
  onSubmit,
  children,
  submitLabel,
  submitting,
  disabled,
  error,
}: AuthFormProps) {
  return (
    <form className="flex w-full flex-col gap-space-24" onSubmit={onSubmit}>
      {error ? (
        <Typography
          variant="text-sm"
          className="rounded-8 border border-border-danger bg-danger-subtle p-space-12 text-error-solid"
        >
          {error}
        </Typography>
      ) : null}
      {children}
      <Button
        type="submit"
        variant="primary"
        size="xl"
        block
        loading={submitting}
        disabled={disabled}
      >
        {submitLabel}
      </Button>
    </form>
  );
}

export type CaptchaScreenBits = {
  isCaptchaAvailable: boolean;
  captchaImage: string | null;
  captchaProvider: string | null;
};

/** Auth0 image captcha when present. Other providers need a widget — see README. */
export function CaptchaField({
  screen,
  value,
  onChange,
}: {
  screen: CaptchaScreenBits;
  value: string;
  onChange: (value: string) => void;
}) {
  if (!screen.isCaptchaAvailable) {
    return null;
  }

  if (!screen.captchaImage) {
    return (
      <Typography variant="text-sm" className="text-error-solid">
        CAPTCHA is required ({screen.captchaProvider ?? 'unknown'}). This screen only supports Auth0
        image captcha — disable bot detection or use the Auth0 provider.
      </Typography>
    );
  }

  return (
    <div className="flex flex-col gap-space-12">
      <img
        className="h-auto max-w-full rounded-8 border border-border"
        src={screen.captchaImage}
        alt=""
      />
      <Field
        id="captcha"
        label="Enter the code shown above"
        value={value}
        autoComplete="off"
        onChange={onChange}
      />
    </div>
  );
}

export function captchaPayload(screen: CaptchaScreenBits, captcha: string): { captcha?: string } {
  if (!screen.isCaptchaAvailable) {
    return {};
  }
  return { captcha };
}

export function isCaptchaReady(screen: CaptchaScreenBits, captcha: string): boolean {
  if (!screen.isCaptchaAvailable) {
    return true;
  }
  if (!screen.captchaImage) {
    return false;
  }
  return captcha.trim().length > 0;
}

export function PasswordRulesList({ rules }: { rules: PasswordRule[] }) {
  return (
    <ul className="mt-space-16 flex flex-col gap-space-12">
      {rules.map((rule) => {
        let iconClass = 'size-icon-md shrink-0 text-tertiary';
        let textClass = 'text-primary ';
        if (rule.ok) {
          iconClass = 'size-icon-md shrink-0 text-brand';
          textClass = 'text-primary';
        }
        return (
          <li key={rule.id} className="flex items-center gap-space-4">
            <CheckCircle2 className={iconClass} aria-hidden />
            <Typography variant="text-sm" weight="regular" className={textClass + ' text-primary'}>
              {rule.label}
            </Typography>
          </li>
        );
      })}
    </ul>
  );
}
